import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { fetchCampaignsList, type Campaign } from '@/store/slices/campaignsSlice';
import { fetchAdvertisingTypes } from '@/store/slices/envsSlice';
import { checkJoinConditions, joinCampaign } from '@/store/slices/myCampaignsSlice';
import { fetchEnvs } from '@/store/slices/envsSlice';
import { showSpinner, hideSpinner } from '@/store/slices/helpersSlice';
import { getChannelTypeTitle } from '@/lib/constants/channelTypes';
import { getRegionLabel } from '@/lib/constants/regions';
import { getLanguageName } from '@/lib/constants/languageIso';
import ChannelTypeIcon from '@/components/icons/ChannelTypeIcon';
import { FaGlobe, FaCalendarAlt, FaBullhorn, FaUser, FaClock, FaMapMarkerAlt, FaVideo } from 'react-icons/fa';
import { MapPinIcon } from 'lucide-react';
import { MdLanguage } from 'react-icons/md';

/**
 * 원본: .old-gc-mono/apps/gc-zoku/components/pages/Campaigns/
 * 참여 가능한 캠페인 목록 페이지
 */

const DEFAULT_BANNER = 'https://gw.alipayobjects.com/zos/rmsportal/JiqGstEfoWAOHiTxclqi.png';

function getLanguages(campaign: Record<string, unknown>): string {
  const langs = campaign.languages as string[] | undefined;
  if (!langs || langs.length === 0) return 'Any';
  return langs.map(getLanguageName).join(', ');
}

function getRegions(campaign: Record<string, unknown>): string {
  const regions = campaign.regions as string[] | undefined;
  if (!regions || regions.length === 0) return 'Any';
  return regions.map(getRegionLabel).join(', ');
}

function Campaigns() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const campaigns = useAppSelector((s) => s.campaigns.activeCampaigns);
  const loading = useAppSelector((s) => s.campaigns.loading);
  const currentUser = useAppSelector((s) => s.envs.currentUser);
  const advertisingTypes = useAppSelector((s) => s.envs.advertisingTypes);
  const checkConditions = useAppSelector((s) => s.myCampaigns.checkJoinConditionsByCampaignId);

  useEffect(() => {
    dispatch(showSpinner());
    Promise.all([
      dispatch(fetchCampaignsList()),
      dispatch(fetchAdvertisingTypes()),
    ]).finally(() => dispatch(hideSpinner()));
  }, [dispatch]);

  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);

  const handleOpenDetails = useCallback((campaign: Campaign) => {
    if (currentUser) {
      const condStatus = checkConditions[campaign.campaignId ?? campaign._id]?.status;
      if (!condStatus) {
        dispatch(checkJoinConditions(campaign.campaignId ?? campaign._id));
      }
    }
    setSelectedCampaign(campaign);
  }, [currentUser, checkConditions, dispatch]);

  const handleJoin = useCallback(async (campaignId: string) => {
    dispatch(showSpinner());
    try {
      await dispatch(joinCampaign(campaignId)).unwrap();
      await dispatch(fetchEnvs()).unwrap();
      setSelectedCampaign(null);
      navigate(`/my-campaigns/${campaignId}`);
    } finally {
      dispatch(hideSpinner());
    }
  }, [dispatch, navigate]);

  if (loading && !campaigns) {
    return <div className="p-6 text-center text-gray-500">Loading...</div>;
  }

  const visibleCampaigns = (campaigns || []).filter((c) => !(c as Record<string, unknown>).hidden);

  if (visibleCampaigns.length === 0) {
    return (
      <div className="p-6">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
          <span className="text-blue-500 text-lg">ℹ</span>
          <div>
            <p className="font-medium text-blue-800">Nothing to show</p>
            <p className="text-sm text-blue-700">Right now we have no active campaigns</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="campaign-list-page">
      <div className="campaign-grid">
        {visibleCampaigns.map((campaign) => {
          const cid = campaign.campaignId ?? campaign._id;
          const campData = campaign as Record<string, unknown>;
          const userCampaigns = (currentUser as Record<string, unknown>)?.campaigns as Array<{ campaignId: string; status: string }> | undefined;
          const joined = userCampaigns?.find((c) => c.campaignId === cid);
          const bannerUrl = ((campData).banner as Array<{ url: string }> | undefined)?.[0]?.url || DEFAULT_BANNER;
          const allowedChannels = (campData.allowedChannelsTypes as string[]) || [];
          const languages = getLanguages(campData);
          const regions = getRegions(campData);

          return (
            <div
              key={cid}
              className="campaign-card"
              onClick={() => handleOpenDetails(campaign)}
            >
              {/* 이미지 영역 + 오버레이 */}
              <div className="campaign-card-cover">
                <img
                  src={bannerUrl}
                  alt={(campData.gameTitle as string) || campaign.name}
                  onError={(e) => { (e.target as HTMLImageElement).src = DEFAULT_BANNER; }}
                />
                <div className="campaign-card-overlay">
                  <div className="overlay-top">
                    <div className="overlay-languages" title={`Languages: ${languages}`}>
                      <MdLanguage size={14} />
                      {languages}
                    </div>
                    <div className="overlay-platforms">
                      {allowedChannels.map((t) => (
                        <ChannelTypeIcon key={t} type={t} size={14} />
                      ))}
                    </div>
                  </div>
                  <div className="overlay-bottom">
                    <div className="overlay-regions" title={`Regions: ${regions}`}>
                      <MapPinIcon size={14} />
                       {regions}
                    </div>
                  </div>
                </div>
              </div>

              {/* 카드 하단 정보 */}
              <div className="campaign-card-body">
                <div className="campaign-card-title">
                  {(campData.gameTitle as string) || campaign.name}
                  {joined && <span className="joined-badge" title="Joined campaign">✓</span>}
                </div>
                <div className="campaign-card-details">DETAILS</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Campaign Details Modal */}
      {selectedCampaign && (
        <CampaignModal
          campaign={selectedCampaign}
          currentUser={currentUser}
          advertisingTypes={advertisingTypes}
          checkConditions={checkConditions}
          onClose={() => setSelectedCampaign(null)}
          onJoin={handleJoin}
        />
      )}
    </div>
  );
}

/* ---------- Labeled field for CampaignDetails ---------- */

function LabeledField({ label, icon, children, className }: { label: string; icon: React.ReactNode; children: React.ReactNode; className?: string }) {
return (
    <div className={`cm-labeled-field${className ? ` ${className}` : ''}`}>
      <div className="cm-labeled-field-label"><span className="cm-lf-icon">{icon}</span>{label}</div>
      <div className="cm-labeled-field-value">{children}</div>
    </div>
  );
}

// 원본과 동일: 백엔드 기준 타임존(America/Los_Angeles)으로 표시
const BACKEND_TZ = 'America/Los_Angeles';

function formatDate(dateStr: string | undefined): string {
  if (!dateStr) return '';
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: BACKEND_TZ,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(new Date(dateStr));
    const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
    return `${get('year')}/${get('month')}/${get('day')}`;
  } catch {
    return String(dateStr);
  }
}

function getJoinedMessage(status: string | undefined): string {
  switch (status) {
    case 'incomplete':
      return 'Please check back to the email thread for details on your status in this campaign.';
    case 'candidate':
    case 'prospect':
    case 'contacting':
    case 'invited':
    case 'negotiation':
    case 'content_creation':
    case 'payment':
    case 'payment_done':
    case 'completed':
      return 'You have joined this campaign.';
    default:
      return "Sorry, you can't join this campaign.";
  }
}

interface CampaignModalProps {
  campaign: Campaign;
  currentUser: unknown;
  advertisingTypes: unknown[] | null;
  checkConditions: Record<string, { status?: string; error?: string }>;
  onClose: () => void;
  onJoin: (campaignId: string) => void;
}

function CampaignModal({ campaign, currentUser, advertisingTypes, checkConditions, onClose, onJoin }: CampaignModalProps) {
  const [closing, setClosing] = useState(false);

  const handleClose = useCallback(() => {
    setClosing(true);
    setTimeout(onClose, 200);
  }, [onClose]);

  const cid = campaign.campaignId ?? campaign._id;
  const condition = checkConditions[cid];
  const isChecking = condition?.status === 'checking';
  const isChecked = condition?.status === 'checked';
  const condError = condition?.error as string | undefined;

  const campData = campaign as Record<string, unknown>;
  const userCampaigns = (currentUser as Record<string, unknown>)?.campaigns as Array<{ campaignId: string; status: string }> | undefined;
  const joined = userCampaigns?.find((c) => c.campaignId === cid);
  const isOpen = Boolean(campData.isOpenApplications);
  const allowedTypes = campData.allowedChannelsTypes as string[] | undefined;
  const userChannels = (currentUser as Record<string, unknown>)?.channels as Array<{ type?: string; channelType?: string }> | undefined;
  const userChannelType = userChannels?.[0]?.type ?? userChannels?.[0]?.channelType;
  const isAllowed = allowedTypes ? allowedTypes.includes(userChannelType || '') : true;
  const bannerUrl = (campData.banner as Array<{ url: string }> | undefined)?.[0]?.url || DEFAULT_BANNER;
  const materialsUrl = (campData.materialsFolder as { url?: string } | undefined)?.url;

  const canJoin = currentUser && isOpen && isAllowed && !joined && isChecked && !condError;

  const pubDates = campData.publicationDates as string[] | undefined;
  const languages = campData.languages as string[] | undefined;
  const regions = campData.regions as string[] | undefined;
  const minSubs = campData.minimumSubscribers as number | undefined;
  const recentActivities = campData.channelRecentActivitiesRequirement as {
    postCount?: number;
    within?: { unitCount?: number; unit?: string };
  } | undefined;
  const advTypesArr = campData.advertisingTypes as string[] | undefined;

  const advTypeLabels = (advTypesArr || []).map((id) => {
    const found = (advertisingTypes || []).find((t) => (t as Record<string, unknown>).id === id) as Record<string, unknown> | undefined;
    return found?.title ? String(found.title) : id.charAt(0).toUpperCase() + id.slice(1);
  });

  return (
    <div className={`cm-overlay${closing ? ' cm-closing' : ''}`} onClick={handleClose}>
      <div className={`cm-modal${closing ? ' cm-closing' : ''}`} onClick={(e) => e.stopPropagation()}>
        {/* 닫기 버튼 */}
        <button className="cm-close" onClick={handleClose} aria-label="Close">&times;</button>

        <div className="cm-body">
          {/* ===== 좌측: 배너 + 상세 정보 ===== */}
          <div className="cm-left">
            <div className="cm-banner">
              <img
                src={bannerUrl}
                alt={(campData.gameTitle as string) || campaign.name}
                onError={(e) => { (e.target as HTMLImageElement).src = DEFAULT_BANNER; }}
              />
            </div>
            <div className="cm-details">
              <div className="cm-details-grid">
                <LabeledField label="Platforms" icon={<FaVideo />}>
                  <div className="cm-platform-icons">
                    {(allowedTypes || []).map((t) => (
                      <ChannelTypeIcon key={t} type={t} size={16} />
                    ))}
                    {(!allowedTypes || allowedTypes.length === 0) && <span>Any</span>}
                  </div>
                </LabeledField>

                <LabeledField label="Language" icon={<FaGlobe />}>
                  {languages && languages.length > 0
                    ? languages.map((l, i) => <div key={i}>{getLanguageName(l)}</div>)
                    : <span>Regional languages</span>
                  }
                </LabeledField>

                <LabeledField label="Publication dates" icon={<FaCalendarAlt />}>
                  {pubDates && pubDates.length >= 2 ? (
                    <>
                      <div>{formatDate(pubDates[0])}</div>
                      <div>{formatDate(pubDates[1])}</div>
                    </>
                  ) : <span>Not specified</span>}
                </LabeledField>

                <LabeledField label="Advertising types" icon={<FaBullhorn />}>
                  {advTypeLabels.length > 0
                    ? advTypeLabels.map((t, i) => <div key={i}>{t}</div>)
                    : <span>Any</span>
                  }
                </LabeledField>

                {typeof minSubs === 'number' && (
                  <LabeledField label="Minimum Subscribers" icon={<FaUser />}>
                    <span>{minSubs.toLocaleString()}</span>
                  </LabeledField>
                )}

                {recentActivities && recentActivities.postCount && (
                  <LabeledField label="Recent Activities" icon={<FaClock />}>
                    <span>
                      {recentActivities.postCount} videos/streams within {recentActivities.within?.unitCount} {recentActivities.within?.unit}s
                    </span>
                  </LabeledField>
                )}

                <LabeledField label="Regions" icon={<FaMapMarkerAlt />} className="cm-lf-full">
                  {regions && regions.length > 0
                    ? regions.map((r, i) => <div key={i}>{getRegionLabel(r)}</div>)
                    : <span>Any</span>
                  }
                </LabeledField>
              </div>
            </div>
          </div>

          {/* ===== 우측: 소개 텍스트 + 알림 ===== */}
          <div className="cm-right">
            {!(isChecked && condError) && (
              <>
                <div className="cm-section">
                  <h3>Introduction</h3>
                  {campData.introduction ? (
                    <div className="cm-html" dangerouslySetInnerHTML={{ __html: campData.introduction as string }} />
                  ) : <p className="cm-empty">-</p>}
                </div>

                <div className="cm-section">
                  <h3>Game</h3>
                  {campData.productDescription ? (
                    <div className="cm-html" dangerouslySetInnerHTML={{ __html: campData.productDescription as string }} />
                  ) : <p className="cm-empty">-</p>}
                </div>

                <div className="cm-section">
                  <h3>Offer details</h3>
                  {campData.offerDetails ? (
                    <div className="cm-html" dangerouslySetInnerHTML={{ __html: campData.offerDetails as string }} />
                  ) : <p className="cm-empty">-</p>}
                </div>

                {materialsUrl && (
                  <div className="cm-section">
                    <h3>Campaign's materials</h3>
                    <a href={materialsUrl} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">
                      Download campaign's materials
                    </a>
                  </div>
                )}
              </>
            )}

            {/* 상태 알림 메시지 */}
            {!isOpen && (
              <div className="cm-alert cm-alert-info">
                This campaign is not accepting applications at this time.
              </div>
            )}
            {isOpen && !isAllowed && !!currentUser && (
              <div className="cm-alert cm-alert-warning">
                Please login using {allowedTypes?.map(getChannelTypeTitle).join(' or ')} in order to join the campaign.
              </div>
            )}
            {joined && (
              <div className="cm-alert cm-alert-info">
                {getJoinedMessage(joined.status)}
              </div>
            )}
            {condError && (
              <div className="cm-alert cm-alert-info">
                {String(condError)}
              </div>
            )}
          </div>
        </div>

        {/* ===== 하단 액션 버튼 ===== */}
        <div className="cm-footer">
          <button className="cm-btn cm-btn-default" onClick={handleClose}>Close</button>
          {currentUser ? (
            <button
              className="cm-btn cm-btn-primary"
              onClick={() => canJoin && onJoin(cid)}
              disabled={!canJoin}
            >
              {isChecking ? 'Checking conditions...' : 'Join campaign'}
            </button>
          ) : (
            <a href="/login" className="cm-btn cm-btn-primary">Login to join</a>
          )}
        </div>
      </div>
    </div>
  );
}

export default Campaigns;
